import { useMemo, useLayoutEffect, useEffect, useState } from 'react';
import {
  Routes,
  Route,
  Link,
  useNavigate,
  useLocation,
  Navigate,
  Outlet,
} from "react-router-dom";
import {
  AppBar,
  Container,
  Box,
  Button,
  IconButton,
  Toolbar,
  Typography,
  createTheme,
  CssBaseline,
  ThemeProvider,
  useTheme,
  Alert,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  CardActions,
} from '@mui/material';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { SnackbarProvider } from 'notistack';
import { useColorScheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import HelpIcon from '@mui/icons-material/Help';
import ReactGA from 'react-ga4';

import Logo from './images/logo-clear-white.svg';
import './App.css';
import { AuthProvider, useAuth } from "./AuthContext";
import LoginPage from "./pages/LoginPage";
import CreateAccount from "./pages/CreateAccount";
import Home from "./pages/Home";
import ResetPassword from './pages/ResetPassword';
import ResetPasswordToken from './pages/ResetPasswordToken';
import AppTheme from './theme/AppTheme';
import { chartsCustomizations } from './theme';

import Header from './components/Header';
import Footer from './components/Footer';
import AuthStatus from './components/AuthStatus';
import Account from "./pages/Account";
import Dashboard from './pages/account/Dashboard';
import Billing from './pages/account/Billing';
import Profile from './pages/account/Profile';
import Rounds from './pages/account/Rounds';
import Checkout from './pages/Checkout';
import CheckoutCanceled from './pages/checkout/CheckoutCanceled';
import CheckoutError from './pages/checkout/CheckoutError';
import CheckoutSuccess from './pages/checkout/CheckoutSuccess';


import "@fontsource/poppins/600.css";
import "@fontsource/poppins/400.css";
import Downloads from './pages/account/Downloads';
import ShotData from './pages/account/ShotData';
import RoundDetail from './pages/account/RoundDetail';
import { APP_TITLE } from './constants';
import SignUp from './pages/SignUp';
import CourseDeveloper from './pages/account/Developer';
import CreateCourse from './pages/account/developer/CreateCourse';
import ManageCourses from './pages/account/developer/ManageCourses';
import EditCourse from './pages/account/developer/EditCourse';
import Simulator from './pages/account/Simulator';
import AccountRemoval from './pages/account/AccountRemoval';
import SimulatorWebGL from './pages/SimulatorWebGL';
import GolfTrakAuth from './pages/GolfTrakAuth';
import SubscribeCancelConfirm from './pages/subscription/SubscribeCancelConfirm';
import SubscribeCanceled from './pages/subscription/SubscribeCanceled';
import SubscribeError from './pages/subscription/SubscribeError';
import SubscribeSuccess from './pages/subscription/SubscribeSuccess';

const xThemeComponents = { ...chartsCustomizations };

ReactGA.initialize('G-7E5L25653W');

// const theme = createTheme({
//   palette: {
//     // background: {
//     //   paper: '#000000',
//     //   default: '#000000',
//     // },
//     // text: {
//     //   primary: "#ffffff"
//     // },
//     mode: 'dark',
//     primary: {
//       main: '#16ab48',
//       contrastText: '#000'
//     },
//     secondary: {
//       main: '#eee'
//     }
//   },
//   typography: {
//     h1: {
//       fontFamily: "Poppins",
//       fontSize: 32
//     },
//     h2: {
//       fontFamily: "Poppins",
//       fontSize: 24
//     },
//     h3: {
//       fontFamily: "Poppins",
//       fontSize: 24
//     },
//     h4: {
//       fontFamily: "Poppins",
//       fontSize: 18,
//       fontWeight: 400
//     }
//   }
// });

function ScrollWrapper({ children }) {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);
  return children;
}

function Layout() {
  const { isPending } = useAuth();
  const theme = useTheme();
  const location = useLocation();

  const [fillBar, setFillBar] = useState('transparent');
  //   return window.scrollY < 10 ? 'transparent' : 'default';
  // }, [window.scrollY]);

  const handleScroll = () => {
    setFillBar(window.scrollY < 10 ? 'transparent' : 'default');
  }
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    }
  }, []);
  if (isPending) {
    return null;
  }
  return (
    <div>
      <Header />

      <Box sx={{ minHeight: '80vh', mt: location.pathname === '/' ? 0 : 12 }}>
        <Outlet />
      </Box>

      <Footer />
    </div>
  );
}

function RequireAuth({ children, employeesOnly }) {
  let auth = useAuth();
  let location = useLocation();
  if (auth.isPending) {
    return null;
  }
  if (!auth.user) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (employeesOnly && !auth.user.isEmployee) {
    return <ErrorPage />;
  }
  return children;
}

function RequireRecaptcha({ children }) {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY}>
      {children}
    </GoogleReCaptchaProvider>
  )
}


function ErrorPage() {
  const navigate = useNavigate();
  return (
    <Container maxWidth="sm">
      <Card>
        <CardHeader slotProps={{ title: { color: 'textDisabled', sx: { mb: 2 } } }} title="404 Ball Not Found" />
        <CardMedia
          sx={{ height: 250 }}
          image="https://media3.giphy.com/media/v1.Y2lkPTc5MGI3NjExaXU5amx5N3ZrbXJjbWQ2Ym5qNmNuc25rZml2dGZrbzBobGRjcGd5biZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/13fTigyJHlacwM/giphy.gif"
        />
        <CardContent sx={{ py: 3 }}>
          <Typography>
            I think you're going to have to take a drop on this one. The page you're looking for may no longer exist.
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: 'center' }}>
          <Button onClick={() => navigate(-1)} variant="outlined" color="secondary">Back</Button>
          <Button onClick={() => navigate('/')} variant="outlined" color="primary">Go Home</Button>
        </CardActions>
      </Card>
    </Container>
  )
}

function PageTitle({ children, title }) {
  useEffect(() => {
    document.title = title ? `${title} | ${APP_TITLE}` : APP_TITLE;
  }, [title]);
  return children;
}

function RouteChangeTracker() {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname });
  }, [location]);

  return null;
}

export default function App() {
  return (
    <AppTheme themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <RouteChangeTracker />
      <SnackbarProvider />
      <ScrollWrapper>
        <AuthProvider>
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<PageTitle><Home /></PageTitle>} />
              <Route path="/login" element={<PageTitle title="Login"><RequireRecaptcha><LoginPage /></RequireRecaptcha></PageTitle>} />
              <Route
                path="/password-reset"
                element={<PageTitle title="Reset Password"><RequireRecaptcha><ResetPassword /></RequireRecaptcha></PageTitle>}
              />
              <Route path="/password-reset/:token" element={<PageTitle title="Reset Password"><RequireRecaptcha><ResetPasswordToken /></RequireRecaptcha></PageTitle>} />
              <Route path="/signup" element={<PageTitle title="Sign Up"><RequireRecaptcha><SignUp /></RequireRecaptcha></PageTitle>} />
              <Route path="/partners/golftrak" element={<GolfTrakAuth />} />

              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/canceled" element={<CheckoutCanceled />} />
              <Route path="/checkout/error" element={<CheckoutError />} />
              <Route path='/*' element={<PageTitle title="Error"><ErrorPage /></PageTitle>} />
            </Route>
            <Route
              path="/account"
              element={
                <RequireAuth>
                  <Account />
                </RequireAuth>
              }
            >
              <Route index element={<PageTitle title="Dashboard"><Dashboard /></PageTitle>} />
              <Route path="rounds" element={<PageTitle title="Rounds"><Rounds /></PageTitle>} />
              <Route path="round/:roundId" element={<PageTitle title="View Round"><RoundDetail /></PageTitle>} />
              <Route path="shot-data" element={<PageTitle title="Shot Data"><ShotData /></PageTitle>} />
              <Route path="billing" element={<PageTitle title="Billing"><Billing /></PageTitle>} />
              <Route path="downloads" element={<PageTitle title="Downloads"><Downloads /></PageTitle>} />
              <Route path="profile" element={<PageTitle title="Profile"><Profile /></PageTitle>} />
              <Route path="simulator" element={<PageTitle title="Simulator"><Simulator /></PageTitle>} />
              <Route path="remove-request" element={<PageTitle title="Remove Account"><AccountRemoval /></PageTitle>} />

              <Route path="developer" element={<PageTitle title="Course Developer"><CourseDeveloper /></PageTitle>}>
                <Route index element={<ManageCourses />} />
                <Route path="create" element={<CreateCourse />} />
                <Route path="edit/:courseId" element={<EditCourse />} />
              </Route>

            </Route>

            <Route path="/subscribe" element={<RequireAuth><Layout /></RequireAuth>}>
              <Route path="success" element={<SubscribeSuccess />} />
              <Route path="error" element={<SubscribeError />} />
              <Route path="cancel/confirm" element={<SubscribeCancelConfirm />} />
              <Route path="cancel/result" element={<SubscribeCanceled />} />
            </Route>
            <Route path="/simulation" element={<RequireAuth><SimulatorWebGL /></RequireAuth>} />

          </Routes>
        </AuthProvider>
      </ScrollWrapper>
    </AppTheme>
  );
}
